import { useMemo, useState } from "react";
import { Clock, Cog } from "lucide-react";
import { fmtEUR, formatEU } from "./formatters";

function SummaryLeg({ label, date, time, loc }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</p>
      <p className="text-xs md:text-sm text-neutral-800 font-medium flex flex-wrap items-center gap-1">
        <span className="tabular-nums">{formatEU(date)} {time}</span>
        <span className="text-neutral-400">·</span>
        <span className="truncate">{loc || "—"}</span>
      </p>
    </div>
  );
}

/**
 * ModalHeroRow — 3-column desktop hero (v3.9.3):
 *   [ main image ] [ vertical thumbs ] [ rental summary card ]
 * All three columns share `h-48` on ≥md; on mobile they stack cleanly
 * (image full-width, thumbs row, summary card below).
 */
export default function ModalHeroRow({ vehicle, form, days, grandTotal }) {
  const images = useMemo(() => {
    const seen = new Set();
    const list = [];
    if (vehicle.image_url) { list.push(vehicle.image_url); seen.add(vehicle.image_url); }
    for (const g of (vehicle.gallery || [])) {
      if (g && !seen.has(g)) { list.push(g); seen.add(g); }
    }
    return list.slice(0, 3);   // max 1 primary + 2 gallery
  }, [vehicle.image_url, vehicle.gallery]);
  const [idx, setIdx] = useState(0);
  const total = images.length;
  return (
    <div
      className="mt-3 grid grid-cols-1 md:grid-cols-[1fr_auto_minmax(220px,300px)] gap-3"
      data-testid="pb-modal-hero-row"
    >
      {/* Col 1 — main image */}
      <div
        className="bg-white rounded-xl border border-neutral-200/60 p-2 flex items-center justify-center h-48 relative overflow-hidden"
        data-testid="pb-modal-main-image"
        onTouchStart={(e) => (e.currentTarget.__x = e.touches[0].clientX)}
        onTouchEnd={(e) => {
          if (total < 2) return;
          const dx = (e.changedTouches[0].clientX - (e.currentTarget.__x || 0));
          if (Math.abs(dx) > 40) setIdx((i) => (i + (dx < 0 ? 1 : -1) + total) % total);
        }}
      >
        {total > 0 ? (
          <img
            src={images[idx]}
            alt={vehicle.name}
            className="object-contain max-h-full max-w-full"
          />
        ) : (
          <Cog size={40} className="text-neutral-300" />
        )}
        {total > 1 && (
          <span className="absolute bottom-2 right-2 text-[10px] font-medium px-2 py-0.5 rounded-full bg-black/55 text-white tabular-nums">
            {idx + 1}/{total}
          </span>
        )}
      </div>

      {/* Col 2 — vertical thumbs (hidden on mobile) */}
      {total > 1 && (
        <div
          className="hidden md:flex flex-col justify-between h-48 w-20 gap-2"
          data-testid="pb-modal-thumbs-vert"
        >
          {images.map((src, i) => (
            <button
              type="button"
              key={i}
              onClick={() => setIdx(i)}
              aria-label={`Show image ${i + 1}`}
              data-testid={`pb-modal-thumb-${i}`}
              className={`flex-1 min-h-0 rounded-lg border overflow-hidden bg-white transition-all ${
                i === idx ? "border-neutral-900 ring-1 ring-neutral-900/10" : "border-neutral-200 opacity-75 hover:opacity-100"
              }`}
            >
              <img src={src} alt="" className="w-full h-full object-contain p-1" />
            </button>
          ))}
        </div>
      )}
      {/* Mobile horizontal thumbs */}
      {total > 1 && (
        <div className="flex md:hidden gap-2 overflow-x-auto pb-1" data-testid="pb-modal-thumbs-horiz">
          {images.map((src, i) => (
            <button
              type="button"
              key={i}
              onClick={() => setIdx(i)}
              aria-label={`Show image ${i + 1}`}
              className={`shrink-0 h-12 w-16 rounded overflow-hidden border-2 transition-all ${
                i === idx ? "border-neutral-900" : "border-transparent opacity-70 hover:opacity-100"
              }`}
            >
              <img src={src} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}

      {/* Col 3 — rental summary */}
      <div
        className="bg-neutral-50 border border-neutral-200/80 rounded-xl p-3 flex flex-col justify-between h-48"
        data-testid="pb-modal-summary"
      >
        <div className="space-y-2">
          <SummaryLeg label="Pickup" date={form.pickup_date} time={form.pickup_time} loc={form.pickup_loc} />
          <SummaryLeg label="Return" date={form.return_date} time={form.return_time} loc={form.return_loc} />
        </div>
        <div className="pt-2 border-t border-neutral-200/80 flex items-end justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-neutral-500">Duration</p>
            <p className="text-sm font-semibold text-neutral-900 flex items-center gap-1">
              <Clock size={12} /> {days} {days === 1 ? "day" : "days"}
            </p>
          </div>
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-widest text-neutral-500">Total</p>
            <p className="text-lg font-bold text-neutral-900 tabular-nums" data-testid="pb-modal-summary-amount">
              {fmtEUR(grandTotal)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
